import { useRepeatingTasks as _useRepeatingTasks } from '@griply/hooks';
import { RepeatingTask } from '@griply/types';
import {
  DeletedFilter,
  DeletedFilterOption,
  filterTasksByLinks,
  isClientDerivableTaskOptions,
  selectTasksByStatus,
} from '@griply/utils';
import { useMemo } from 'react';
import { GetTaskOptions } from 'shared/services/backend-api';
import { TaskType } from 'shared/types/task-base';

import { useAllRepeatingTasks } from './use-all-repeating-tasks';
import { useLegacyTasksOfType } from './use-legacy-tasks-of-type';

export type Options = Omit<GetTaskOptions, 'type'> & DeletedFilterOption;

export const useRepeatingTasks = ({
  deletedFilter = DeletedFilter.ExcludeDeleted,
  ...options
}: Options = {}): RepeatingTask[] => {
  const derivable = isClientDerivableTaskOptions(options);
  const baseRepeatingTasks = useAllRepeatingTasks(deletedFilter);
  const legacyTasks = useLegacyTasksOfType(
    TaskType.Repeating,
    options,
    deletedFilter,
    !derivable,
  );
  const legacyRepeatingTasks = _useRepeatingTasks({ tasks: legacyTasks });

  const {
    uncompletedOnly,
    completedOnly,
    unArchivedOnly,
    unFrozenOnly,
    archivedOnly,
    goals,
    lifeAreas,
  } = options;

  const derivedRepeatingTasks = useMemo(
    () =>
      filterTasksByLinks(
        selectTasksByStatus(baseRepeatingTasks, {
          uncompletedOnly,
          completedOnly,
          unArchivedOnly,
          unFrozenOnly,
          archivedOnly,
        }) ?? baseRepeatingTasks,
        { goals, lifeAreas },
      ),
    [
      baseRepeatingTasks,
      uncompletedOnly,
      completedOnly,
      unArchivedOnly,
      unFrozenOnly,
      archivedOnly,
      goals,
      lifeAreas,
    ],
  );

  return derivable ? derivedRepeatingTasks : legacyRepeatingTasks;
};
